import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaGithub, FaExternalLinkAlt, FaTimes, FaJava, FaReact, FaNodeJs,
  FaDocker, FaAws, FaCode, FaServer, FaHospital, FaTasks, FaBook,
  FaGamepad, FaUtensils, FaUniversity, FaShoppingCart, FaCheckCircle,
  FaCogs, FaLayerGroup, FaMapMarkedAlt, FaHandHoldingHeart
} from 'react-icons/fa';
import {
  SiSpringboot, SiMongodb, SiMysql, SiRedis, SiTailwindcss,
  SiExpress, SiJsonwebtokens, SiRazorpay, SiPostman, SiSwagger, SiGit
} from 'react-icons/si';
import './Projects.css';

/* ------------------------------------------------------------------ */
/*  DESKTOP WALL SLOTS (1600 x 800 stage)                              */
/* ------------------------------------------------------------------ */
const DESKTOP_STAGE_W = 1600;
const DESKTOP_STAGE_H = 800;
const PROJECT_SLOTS_DESKTOP = [
  { cx: 800,  cy: 150, w: 400, h: 215, ry: 0,   rz: 0,  z: 6 },
  { cx: 440,  cy: 140, w: 290, h: 180, ry: 18,  rz: 3,  z: 4 },
  { cx: 1160, cy: 140, w: 290, h: 180, ry: -18, rz: -3, z: 4 },
  { cx: 315,  cy: 345, w: 250, h: 175, ry: 16,  rz: 3,  z: 4 },
  { cx: 1285, cy: 345, w: 250, h: 175, ry: -16, rz: -3, z: 4 },
  { cx: 400,  cy: 590, w: 270, h: 170, ry: 8,   rz: 2,  z: 4 },
  { cx: 1200, cy: 590, w: 270, h: 170, ry: -8,  rz: -2, z: 4 },
  { cx: 570,  cy: 385, w: 210, h: 145, ry: 10,  rz: 2,  z: 3 },
  { cx: 1030, cy: 385, w: 210, h: 145, ry: -10, rz: -2, z: 3 }
];

/* ------------------------------------------------------------------ */
/*  MOBILE WALL SLOTS (800 x 1100 logical portrait stage)              */
/* ------------------------------------------------------------------ */
const MOBILE_STAGE_W = 800;
const MOBILE_STAGE_H = 1200;
const PROJECT_SLOTS_MOBILE = [
  /* Row 1: featured big */
  { cx: 400, cy: 150,  w: 480, h: 250, ry: 0,  rz: 0,  z: 8 }, // 1 Bhoomi

  /* Row 2: featured pair */
  { cx: 205, cy: 400,  w: 350, h: 185, ry: 6,  rz: 1,  z: 7 }, // 2 Majhi
  { cx: 595, cy: 400,  w: 350, h: 185, ry: -6, rz: -1, z: 7 }, // 3 MediBook

  /* Row 3 */
  { cx: 150, cy: 620,  w: 270, h: 150, ry: 6,  rz: 1,  z: 5 }, // 4 Bankify
  { cx: 650, cy: 620,  w: 270, h: 150, ry: -6, rz: -1, z: 5 }, // 5 ShopSmart

  /* Row 4 */
  { cx: 150, cy: 810,  w: 270, h: 150, ry: 6,  rz: 1,  z: 5 }, // 6 TaskFlow
  { cx: 650, cy: 810,  w: 270, h: 150, ry: -6, rz: -1, z: 5 }, // 7 FoodDelivery

  /* Row 5 */
  { cx: 150, cy: 1000, w: 270, h: 150, ry: 6,  rz: 1,  z: 5 }, // 8 E-Book
  { cx: 650, cy: 1000, w: 270, h: 150, ry: -6, rz: -1, z: 5 }  // 9 GuessPro
];

/* ------------------------------------------------------------------ */
/*  PROJECT DATA                                                      */
/* ------------------------------------------------------------------ */
const projectsData = [
  {
    id: 1, title: 'Bhoomi Seva', subtitle: 'Smart Land Records Management & AI Valuation System',
    icon: <FaMapMarkedAlt />, category: 'fullstack', featured: true, color: '#F97316',
    shortDescription: 'Enterprise-grade land records platform for Maharashtra. Instant digital 7/12 Satbara, 8-A Extract and Property Card, plus AI-powered land price prediction.',
    metrics: { Uptime: '99.5%+', Response: '<200ms', APIs: '25+', Records: '1000+' },
    features: [
      { name: 'Smart Land Search', description: 'Search by survey no, village, taluka or land type with filters' },
      { name: 'Document Generation', description: 'One-click 7/12 Satbara, 8-A Extract and Property Card' },
      { name: 'AI Price Prediction', description: 'XGBoost model predicts market value from area, RR rate and distances' },
      { name: 'Download History', description: 'Track all viewed lands and downloaded PDF documents' },
      { name: 'Land Records CRUD', description: 'Admin adds, edits and deletes records with geo-coordinates' },
      { name: 'User Management', description: 'Block, unblock or delete citizens with cascade cleanup' }
    ],
    tech: ['React 18', 'Vite', 'Tailwind CSS', 'Java 17', 'Spring Boot 3.x', 'Python Flask', 'XGBoost', 'MySQL 8.0', 'TiDB Cloud'],
    github: 'https://github.com/AmitIngAI/bhoomi-seva',
    live: 'https://bhoomi-frontend-f5sw.onrender.com/'
  },
  {
    id: 2, title: 'Majhi Yojna', subtitle: 'AI-Powered Government Scheme Recommendation System',
    icon: <FaHandHoldingHeart />, category: 'fullstack', featured: true, color: '#14B8A6',
    shortDescription: 'AI platform that matches Maharashtra citizens with the government schemes they are eligible for, using rule-based filtering and Cosine Similarity ranking.',
    metrics: { Uptime: '99.5%+', Response: '<300ms', APIs: '20+', Schemes: '30+' },
    features: [
      { name: 'AI Recommendations', description: 'Ranked schemes with match percentage and matched rules' },
      { name: 'Life Event Suggestions', description: 'Suggestions based on age, gender, occupation, BPL, disability' },
      { name: 'Eligibility Checker', description: 'What-if simulator for different profile scenarios' },
      { name: 'Scheme Comparison', description: 'Side-by-side comparison of multiple schemes' },
      { name: 'Family Dashboard', description: 'Save schemes and view recommendations for family' },
      { name: 'Profile Completion', description: '11-field score that unlocks recommendations' }
    ],
    tech: ['React 18', 'Context API', 'Java 17', 'Spring Boot 3.x', 'Python Flask', 'Scikit-learn', 'MySQL 8.0', 'TiDB Cloud', 'JWT'],
    github: 'https://github.com/AmitIngAI/Majhi-Yojna.ai',
    live: 'https://majhi-yojna-frontend.onrender.com/'
  },
  {
    id: 3, title: 'MediBook Pro', subtitle: 'Healthcare Appointment Management System',
    icon: <FaHospital />, category: 'fullstack', featured: true, color: '#00D9A5',
    shortDescription: 'Healthcare platform for doctor-patient interaction with real-time appointment scheduling, secure payments and digital medical records.',
    metrics: { Uptime: '99.9%', Response: '<100ms', APIs: '15+', Users: '500+' },
    features: [
      { name: 'Smart Doctor Search', description: 'Filter by specialization, location, rating and availability' },
      { name: 'Real-time Slot Booking', description: 'Live availability and instant appointment booking' },
      { name: 'Secure Payments', description: 'Online payments with Razorpay integration' },
      { name: 'Appointment Management', description: 'View, reschedule or cancel with refund processing' },
      { name: 'Digital Prescriptions', description: 'Doctors create and send prescriptions to patients' },
      { name: 'Admin Analytics', description: 'Platform-wide metrics and doctor verification' }
    ],
    tech: ['React.js 18', 'Material UI v5', 'Java 17', 'Spring Boot 3.0', 'MySQL 8.0', 'Redis 7.0', 'Razorpay API', 'Twilio SMS', 'JWT Authentication'],
    github: 'https://github.com/AmitIngAI/MediBook-Pro',
    live: 'https://medibook-pro-front.onrender.com/'
  },
  {
    id: 4, title: 'Bankify', subtitle: 'Modern Digital Banking Platform',
    icon: <FaUniversity />, category: 'fullstack', featured: false, color: '#667EEA',
    shortDescription: 'Secure digital banking with account management, real-time money transfers, card services and spending tracking.',
    metrics: { Security: 'Bank Grade', Uptime: '99.99%', Response: '<150ms', Encryption: 'AES-256' },
    features: [
      { name: 'Multi-Account Dashboard', description: 'All accounts with real-time balances' },
      { name: 'Instant Transfers', description: 'Between accounts, UPI and bank transfers' },
      { name: 'Virtual Cards', description: 'Issue, freeze and manage virtual debit cards' },
      { name: 'Spending Analytics', description: 'Visual breakdown of expenses by category' },
      { name: 'Two-Factor Auth', description: 'OTP verification for sensitive operations' },
      { name: 'Fraud Detection', description: 'Suspicious activity monitoring and audit logs' }
    ],
    tech: ['React.js 18', 'Redux Toolkit', 'Tailwind CSS', 'Java 17', 'Spring Boot 3.0', 'Spring Security', 'MySQL 8.0', 'JWT'],
    github: 'https://github.com/AmitIngAI',
    live: 'https://bankify-demo-digital-banking-system.vercel.app/'
  },
  {
    id: 5, title: 'ShopSmart', subtitle: 'Microservices E-Commerce Platform',
    icon: <FaShoppingCart />, category: 'fullstack', featured: false, color: '#F59E0B',
    shortDescription: 'Scalable e-commerce platform with independent services for users, products, orders and payments.',
    metrics: { Services: '4', Products: '10,000+', Availability: '99.9%', Scaling: 'Horizontal' },
    features: [
      { name: 'Product Discovery', description: 'Browse with advanced search and filters' },
      { name: 'Shopping Cart', description: 'Persistent cart with real-time price updates' },
      { name: 'Secure Checkout', description: 'Streamlined checkout with multiple payment options' },
      { name: 'Order Tracking', description: 'Real-time order status and delivery updates' },
      { name: 'Seller Dashboard', description: 'Sales analytics and inventory management' },
      { name: 'Circuit Breaker', description: 'Fault tolerance that prevents cascade failures' }
    ],
    tech: ['React.js 18', 'Redux Toolkit', 'Node.js 18', 'Express.js', 'MongoDB Atlas', 'Docker', 'Stripe API', 'Redis'],
    github: 'https://github.com/AmitIngAI',
    live: 'https://shopssmart-ecommerce.vercel.app/'
  },
  {
    id: 6, title: 'TaskFlow Ultimate', subtitle: 'Project Management Platform',
    icon: <FaTasks />, category: 'mern', featured: false, color: '#8B5CF6',
    shortDescription: 'Project management tool inspired by Trello and Jira with Kanban boards, deadlines and real-time team collaboration.',
    metrics: { Realtime: 'Yes', Latency: '<100ms', Users: '50+', Boards: 'Multiple' },
    features: [
      { name: 'Kanban Boards', description: 'Visual drag-and-drop task organization' },
      { name: 'Priority Levels', description: 'High, medium and low with colour coding' },
      { name: 'Due Dates', description: 'Deadline setting with reminder notifications' },
      { name: 'Team Workspaces', description: 'Isolated workspaces with role management' },
      { name: 'Comments', description: 'Discussion threads and @mentions on tasks' },
      { name: 'Progress Dashboards', description: 'Visual project completion metrics' }
    ],
    tech: ['React.js 18', 'Redux Toolkit', 'React DnD', 'Socket.io', 'Node.js 18', 'Express.js', 'MongoDB Atlas', 'JWT'],
    github: 'https://github.com/AmitIngAI',
    live: 'https://taskflow-frontend-ziha.onrender.com/'
  },
  {
    id: 7, title: 'FoodDelivery Pro', subtitle: 'MERN Stack Food Delivery Platform',
    icon: <FaUtensils />, category: 'mern', featured: false, color: '#EF4444',
    shortDescription: 'Food ordering platform connecting customers with local restaurants, with order tracking and a restaurant dashboard.',
    metrics: { Restaurants: '100+', Orders: '500+/day', Uptime: '99.5%', Payments: 'Stripe' },
    features: [
      { name: 'Restaurant Discovery', description: 'Browse nearby restaurants with filters' },
      { name: 'Smart Cart', description: 'Easy item management with customizations' },
      { name: 'Secure Checkout', description: 'Multiple payment options and saved addresses' },
      { name: 'Order Tracking', description: 'Real-time order status updates' },
      { name: 'Menu Management', description: 'Restaurants add and organize menu items' },
      { name: 'Order Dashboard', description: 'Live incoming order notifications' }
    ],
    tech: ['React.js 18', 'Redux Toolkit', 'Node.js 18', 'Express.js', 'MongoDB Atlas', 'Stripe API', 'Cloudinary', 'JWT'],
    github: 'https://github.com/AmitIngAI',
    live: 'https://food-delivery-online.vercel.app/'
  },
  {
    id: 8, title: 'E-Book Library Pro', subtitle: 'Digital Library Management System',
    icon: <FaBook />, category: 'mern', featured: false, color: '#10B981',
    shortDescription: 'Online digital library to discover, read and download ebooks with category-based organization.',
    metrics: { Books: '1000+', Categories: '20+', Formats: 'PDF, ePub', Sync: 'Real-time' },
    features: [
      { name: 'Online Reader', description: 'Browser-based ebook reader with customization' },
      { name: 'Reading Progress', description: 'Auto-sync progress across devices' },
      { name: 'Bookmarks', description: 'Mark important pages for quick access' },
      { name: 'Highlights & Notes', description: 'Annotate while reading' },
      { name: 'Category Browse', description: 'Books organized by genres and topics' },
      { name: 'Search', description: 'Find books by title or author instantly' }
    ],
    tech: ['React.js 18', 'Tailwind CSS', 'Node.js 18', 'Express.js', 'MongoDB Atlas', 'AWS S3', 'PDF.js', 'ePub.js'],
    github: 'https://github.com/AmitIngAI',
    live: 'https://ebook-library-online.vercel.app/'
  },
  {
    id: 9, title: 'GuessPro', subtitle: 'Interactive Number Guessing Game',
    icon: <FaGamepad />, category: 'mini', featured: false, color: '#EC4899',
    shortDescription: 'Browser-based number guessing game with difficulty levels, score tracking and a local leaderboard.',
    metrics: { Levels: '3', Load: '<1s', Size: '<50KB', Backend: 'None' },
    features: [
      { name: 'Random Numbers', description: 'Random target number every round' },
      { name: 'Difficulty Levels', description: 'Easy (1-50), Medium (1-100), Hard (1-500)' },
      { name: 'Hint System', description: 'Higher / lower feedback after each guess' },
      { name: 'Score Calculation', description: 'Points based on attempts and time' },
      { name: 'High Score Board', description: 'Leaderboard saved in the browser' },
      { name: 'Sound Effects', description: 'Audio and visual feedback for actions' }
    ],
    tech: ['HTML5', 'CSS3', 'JavaScript ES6+', 'LocalStorage API', 'Web Audio API'],
    github: 'https://github.com/AmitIngAI',
    live: 'https://guess-my-number-game-two-jet.vercel.app/'
  }
];

const categories = [
  { id: 'all', label: 'All Projects', icon: <FaLayerGroup /> },
  { id: 'fullstack', label: 'Full Stack', icon: <FaServer /> },
  { id: 'mern', label: 'MERN Stack', icon: <FaReact /> },
  { id: 'mini', label: 'Mini Projects', icon: <FaGamepad /> }
];

const TECH_ICONS = {
  'React.js': <FaReact />, 'React.js 18': <FaReact />, 'React 18': <FaReact />,
  'Node.js 18': <FaNodeJs />, 'Express.js': <SiExpress />,
  'Spring Boot 3.x': <SiSpringboot />, 'Spring Boot 3.0': <SiSpringboot />, 'Spring Security': <SiSpringboot />,
  'MongoDB Atlas': <SiMongodb />, 'MySQL 8.0': <SiMysql />, 'Redis': <SiRedis />, 'Redis 7.0': <SiRedis />,
  'Tailwind CSS': <SiTailwindcss />, JWT: <SiJsonwebtokens />, 'JWT Authentication': <SiJsonwebtokens />,
  'Java 17': <FaJava />, Docker: <FaDocker />, 'AWS S3': <FaAws />,
  'Razorpay API': <SiRazorpay />, Postman: <SiPostman />, 'Swagger UI': <SiSwagger />, Git: <SiGit />
};

const TECH_COLORS = {
  'React.js': '#61DAFB', 'React.js 18': '#61DAFB', 'React 18': '#61DAFB',
  'Node.js 18': '#68D391', 'Express.js': '#9AE6B4', 'Spring Boot 3.x': '#6DB33F', 'Spring Boot 3.0': '#6DB33F',
  'Spring Security': '#6DB33F', 'MongoDB Atlas': '#47A248', 'MySQL 8.0': '#4DA3D4', Redis: '#DC382D',
  'Redis 7.0': '#DC382D', 'Tailwind CSS': '#06B6D4', JWT: '#D63AFF', 'JWT Authentication': '#D63AFF',
  'Java 17': '#ED8B00', Docker: '#2496ED', 'AWS S3': '#FF9900', 'Razorpay API': '#3395FF',
  'Stripe API': '#8B85FF', 'Redux Toolkit': '#A78BFA', HTML5: '#E34F26', CSS3: '#1572B6',
  'JavaScript ES6+': '#F7DF1E', 'Python Flask': '#E5E7EB', XGBoost: '#00D9A5'
};

const hideBrokenImage = (e) => {
  e.currentTarget.style.display = 'none';
};

const pmStagger = { hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.15 } } };
const pmRise = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const stageRef = useRef(null);
  const location = useLocation();

  // Detect Mobile View for Slots
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const term = searchTerm.toLowerCase();
  const isMatch = (project) => {
    const okFilter = filter === 'all' || project.category === filter;
    const okSearch =
      project.title.toLowerCase().includes(term) ||
      project.subtitle.toLowerCase().includes(term) ||
      project.tech.some((t) => t.toLowerCase().includes(term));
    return okFilter && okSearch;
  };
  const filteredProjects = projectsData.filter(isMatch);

  useEffect(() => {
    const id = location.state && location.state.openId;
    if (!id) return;
    const p = projectsData.find((x) => x.id === id);
    if (p) setSelectedProject(p);
  }, [location.state]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedProject(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedProject ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedProject]);

  const onStageMove = (e) => {
    if (isMobile) return; 
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', (((e.clientX - r.left) / r.width) * 2 - 1).toFixed(3));
    el.style.setProperty('--my', (((e.clientY - r.top) / r.height) * 2 - 1).toFixed(3));
  };
  
  const onStageLeave = () => {
    const el = stageRef.current;
    if (!el) return;
    el.style.setProperty('--mx', '0');
    el.style.setProperty('--my', '0');
  };

  const badgeText = (p) =>
    p.featured ? '★ Featured' : p.category === 'fullstack' ? 'Full Stack' : p.category === 'mern' ? 'MERN' : 'Mini';

  const sp = selectedProject;
  
  // Decide which slots to use
  const STAGE_W = isMobile ? MOBILE_STAGE_W : DESKTOP_STAGE_W;
  const STAGE_H = isMobile ? MOBILE_STAGE_H : DESKTOP_STAGE_H;
  const activeSlots = isMobile ? PROJECT_SLOTS_MOBILE : PROJECT_SLOTS_DESKTOP;

  return (
    <div className="pw-page">
      {/* ================= CINEMATIC WALL (Laptop & Mobile) ================= */}
      <section className="pw-scene">
        <div className="pw-stage" ref={stageRef} onMouseMove={onStageMove} onMouseLeave={onStageLeave}>
          <div className="pw-bg" style={{ backgroundImage: 'url(/images/projects-bg.jpg)' }} />
          <div className="pw-vignette" />

          {/* Desktop specific corners/labels */}
          {!isMobile && (
            <>
              <div className="pw-label"><span>Projects</span></div>
              <div className="pw-corner pw-corner-tr"><span>Scroll</span><span>Explore</span><span>Interact</span></div>
              <div className="pw-corner pw-corner-bl"><span>Real</span><span>Projects</span><span>Real</span><span>Stories</span></div>
              <div className="pw-corner pw-corner-br"><span>Designing</span><span>a brighter</span><span>tomorrow</span></div>
            </>
          )}

          {/* Filters (Desktop left side, Mobile Top) */}
          <nav className="pw-menu" aria-label="Filter projects">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                className={filter === cat.id ? 'active' : ''}
                onClick={() => setFilter(cat.id)}
              >
                {isMobile ? cat.icon : ''} {cat.label}
                <em>{cat.id === 'all' ? projectsData.length : projectsData.filter((p) => p.category === cat.id).length}</em>
              </button>
            ))}
          </nav>

          {/* Search (Desktop bottom center, Mobile bottom) */}
          <div className="pw-search">
            <input
              type="text"
              placeholder="SEARCH PROJECTS OR TECH"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search projects"
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} aria-label="Clear search"><FaTimes /></button>
            )}
          </div>

          {projectsData.map((project, i) => {
            const slot = activeSlots[i];
            if (!slot) return null;
            const matched = isMatch(project);
            return (
              <div
                key={project.id}
                className={`pw-screen ${matched ? '' : 'dim'} ${i < 1 ? 'big' : ''}`}
                style={{
                  left: `${(slot.cx / STAGE_W) * 100}%`,
                  top: `${(slot.cy / STAGE_H) * 100}%`,
                  width: `${(slot.w / STAGE_W) * 100}%`,
                  aspectRatio: `${slot.w} / ${slot.h}`,
                  zIndex: slot.z,
                  '--ry': `${slot.ry}deg`,
                  '--rz': `${slot.rz}deg`,
                  '--c': project.color,
                  '--cg': `${project.color}88`,
                  '--delay': `${(i * 0.7).toFixed(1)}s`
                }}
                role="button"
                tabIndex={matched ? 0 : -1}
                aria-label={`Open ${project.title}`}
                onClick={() => matched && setSelectedProject(project)}
                onKeyDown={(e) => {
                  if (matched && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setSelectedProject(project);
                  }
                }}
              >
                <div className="pw-float">
                  <div className="pw-frame">
                    <img src={`/images/proj-${project.id}.jpg`} alt="" onError={hideBrokenImage} draggable="false" />
                    <div className="pw-shade" />
                    <div className="pw-copy">
                      <span className="pw-badge">{badgeText(project)}</span>
                      <h3>{project.title}</h3>
                      <p>{project.subtitle}</p>
                      <span className="pw-explore">Explore</span>
                    </div>
                    <div className="pw-glass" />
                  </div>
                </div>
              </div>
            );
          })}

          {filteredProjects.length === 0 && (
            <div className="pw-empty">
              <h3>No Projects Found</h3>
              <p>Try adjusting your search term or filter selection</p>
              <button type="button" onClick={() => { setSearchTerm(''); setFilter('all'); }}>
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </section>

    

      {/* ================= PROJECT MODAL (single page) ================= */}
      <AnimatePresence>
        {sp && (
          <>
            <motion.div
              key="pm-backdrop"
              className="pm-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
            />
            <div className="pm-wrap" key="pm-wrap">
              <motion.div
                className="pm-modal"
                style={{ '--pc': sp.color }}
                initial={{ opacity: 0, y: 40, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 30, scale: 0.96 }}
                transition={{ type: 'spring', damping: 26, stiffness: 260 }}
              >
                <span className="pm-bar" />
                <button type="button" className="pm-close" onClick={() => setSelectedProject(null)} aria-label="Close">
                  <FaTimes />
                </button>

                <motion.div className="pm-body" variants={pmStagger} initial="hidden" animate="show">
                  <motion.header variants={pmRise} className="pm-head">
                    <div className="pm-icon">{sp.icon}</div>
                    <div>
                      <span className="pm-tag">
                        {sp.featured ? '★ Featured' : sp.category === 'fullstack' ? 'Full Stack' : sp.category === 'mern' ? 'MERN Stack' : 'Mini Project'}
                      </span>
                      <h2>{sp.title}</h2>
                      <p>{sp.subtitle}</p>
                    </div>
                  </motion.header>

                  <motion.p variants={pmRise} className="pm-desc">{sp.shortDescription}</motion.p>

                  <motion.div variants={pmRise} className="pm-metrics">
                    {Object.entries(sp.metrics).map(([k, v]) => (
                      <div key={k} className="pm-metric"><b>{v}</b><span>{k}</span></div>
                    ))}
                  </motion.div>

                  <motion.div variants={pmRise}>
                    <h4 className="pm-h"><FaCheckCircle /> Key Features</h4>
                    <div className="pm-feats">
                      {sp.features.map((f) => (
                        <div key={f.name} className="pm-feat">
                          <h5><i>✦</i>{f.name}</h5>
                          <p>{f.description}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  <motion.div variants={pmRise}>
                    <h4 className="pm-h"><FaCogs /> Tech Stack</h4>
                    <div className="pm-tech">
                      {sp.tech.map((t) => (
                        <span key={t}>
                          <em style={{ color: TECH_COLORS[t] || sp.color }}>{TECH_ICONS[t] || <FaCode />}</em>
                          {t}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>

                <div className="pm-actions">
                  <a href={sp.github} target="_blank" rel="noopener noreferrer" className="pm-btn pm-git">
                    <FaGithub /><span>Source Code</span>
                  </a>
                  {sp.live && (
                    <a href={sp.live} target="_blank" rel="noopener noreferrer" className="pm-btn pm-live">
                      <FaExternalLinkAlt /><span>Live Demo</span>
                    </a>
                  )}
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Projects;